import React, { useEffect, useState } from 'react'
import { MemoizedParticipantGrid } from '@/components/ParticipantGrid'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { useHall } from '@/contexts/HallProvider'
import { TopInfoBar } from '@/components/TopInfoBar'
import { useTurns } from '@/contexts/TurnsProvider'
import { capitalizeEveryWord } from '@/utils/helper'
import useIsMobile from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'
import { useMeetingAppContext } from '@/MeetingAppContextDef'
import { useMeeting } from '@videosdk.live/react-sdk'

type ParticipantsViewerProps = {
  isPresenting: boolean
  sidebarRef: React.RefObject<HTMLDivElement>
  ignorePins?: boolean
}

const GridWrapper = ({
  children,
  className = '',
  style = {},
  id = '',
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  id?: string
}) => (
  <div id={id} className={cn('p-2 flex-1 min-h-0', className)} style={style}>
    {children}
  </div>
)

const TeamSection = ({
  title,
  bgColor,
  borderColor,
  participantIds,
  isPresenting,
  containerId,
  className = '',
  emptyPlaceholderSrc,
}: {
  title?: string
  bgColor: string
  borderColor: string
  participantIds: string[]
  isPresenting: boolean
  containerId: string
  className?: string
  emptyPlaceholderSrc?: string
}) => (
  <div
    id={containerId}
    className={cn(
      'flex flex-col flex-1 text-primary rounded-lg bg-opacity-50 border-2 w-full min-h-0',
      borderColor,
      className,
    )}
  >
    <span
      className={cn(
        'w-full text-left py-1 pl-4 font-semibold tracking-wider text-white sticky top-0 z-10',
        bgColor,
      )}
    >
      {capitalizeEveryWord(title)}
    </span>
    <div className="flex-1 overflow-y-auto overflow-x-hidden noScrollbar p-2 min-h-0">
      <div className="flex justify-center w-full h-full">
        <MemoizedParticipantGrid
          participantIds={participantIds}
          isPresenting={isPresenting}
          type="pinnedObservers"
          containerId={containerId}
          emptyPlaceholderSrc={emptyPlaceholderSrc || '/Empty Chair - No card.png'}
        />
      </div>
    </div>
  </div>
)

function ParticipantsViewer({
  isPresenting,
  sidebarRef,
  ignorePins = false,
}: ParticipantsViewerProps) {
  const { affirmativeUsers, negativeUsers, moderatorUsers, removePinnedHallUsers, getUser, isBot } =
    useHallUsers()
  const { pinnedParticipants } = useTurns()
  const { hall } = useHall()
  const { sideBarMode } = useMeetingAppContext()
  const { participants } = useMeeting()

  const [sidebarWidth, setSidebarWidth] = useState('98vw')
  const isMobile = useIsMobile()

  useEffect(() => {
    if (!sidebarRef?.current) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const maxWidth = `calc(100vw - ${entry.contentRect.width}px)`
        setSidebarWidth(maxWidth)
      }
    })
    observer.observe(sidebarRef.current)
    return () => observer.disconnect()
  }, [sidebarRef])

  const maxWrapperWidth = { maxWidth: sideBarMode ? sidebarWidth : '98vw' }

  const validPinned: string[] = !ignorePins
    ? pinnedParticipants.filter((id: string) => {
        const hallUser = getUser(id)
        if (!hallUser) return false
        if (isBot(hallUser)) return true
        return !!hallUser.videoSDKId && participants.has(hallUser.videoSDKId)
      })
    : []

  if (validPinned.length > 0) {
    // Compute remaining users per team (excluding pinned), to decide which placeholder to show
    const affRemaining = removePinnedHallUsers(affirmativeUsers)
    const negRemaining = removePinnedHallUsers(negativeUsers)
    const affHasPinned = pinnedParticipants.some((id: string) => affirmativeUsers.includes(id))
    const negHasPinned = pinnedParticipants.some((id: string) => negativeUsers.includes(id))
    const affPlaceholder = affRemaining.length === 0 && affHasPinned ? '/Empty Chair.png' : undefined
    const negPlaceholder = negRemaining.length === 0 && negHasPinned ? '/Empty Chair.png' : undefined
    return (
      <div className="w-full flex flex-col overflow-hidden min-h-0" style={maxWrapperWidth}>
        <TopInfoBar />
        {isMobile ? (
          <div className="flex flex-col h-full min-h-0 gap-2 p-2">
            <GridWrapper id="pinned-grid" className="rounded-lg overflow-hidden flex-[1.6]">
              <div className="w-full justify-center flex h-full flex-1">
                <MemoizedParticipantGrid
                  participantIds={validPinned}
                  isPresenting={isPresenting}
                  containerId="pinned-grid"
                  type="pinned"
                  emptyPlaceholderSrc="/Empty Chair.png"
                />
              </div>
            </GridWrapper>

            <TeamSection
              title={hall?.affirmingTeamName}
              bgColor="bg-affirmative"
              borderColor="border-affirmative"
              participantIds={affRemaining}
              isPresenting={isPresenting}
              containerId="aff-grid"
              className="flex-[1]"
              emptyPlaceholderSrc={affPlaceholder}
            />
            <TeamSection
              title={hall?.dissentingTeamName}
              bgColor="bg-negative"
              borderColor="border-negative"
              participantIds={negRemaining}
              isPresenting={isPresenting}
              containerId="neg-grid"
              className="flex-[1]"
              emptyPlaceholderSrc={negPlaceholder}
            />

            {moderatorUsers.length > 0 && (
              <div className="flex flex-col flex-[0.6] rounded-lg overflow-hidden border-2 border-purple-500 min-h-0">
                <span className="w-full text-left py-1 pl-4 font-semibold tracking-wider text-white sticky top-0 z-10 bg-purple-600">
                  Judges & Moderators
                </span>
                <GridWrapper id="mod-grid" className="bg-gray-200 dark:bg-secondary overflow-hidden flex-1 p-2">
                  <MemoizedParticipantGrid
                    participantIds={removePinnedHallUsers(moderatorUsers)}
                    isPresenting={isPresenting}
                    type="moderators"
                    containerId="mod-grid"
                  />
                </GridWrapper>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col md:flex-row h-full min-h-0">
            <div className="flex flex-col px-2 md:w-[60%] lg:w-[60%] min-h-0">
              <GridWrapper id="pinned-grid" className="rounded-lg overflow-hidden flex-[7]">
                <div className="w-full justify-center flex h-full flex-1">
                  <MemoizedParticipantGrid
                    participantIds={validPinned}
                    isPresenting={isPresenting}
                    containerId="pinned-grid"
                    type="pinned"
                    emptyPlaceholderSrc="/Empty Chair.png"
                  />
                </div>
              </GridWrapper>

              <div className="w-full flex flex-col rounded-lg overflow-hidden border-2 border-purple-500 flex-[3]">
                <span className="w-full text-left py-1 pl-4 font-semibold tracking-wider text-white sticky top-0 z-10 bg-purple-600">
                  Judges & Moderators
                </span>
                <GridWrapper id="mod-grid" className="bg-gray-200 dark:bg-secondary overflow-hidden flex-1 p-2">
                  <MemoizedParticipantGrid
                    participantIds={removePinnedHallUsers(moderatorUsers)}
                    isPresenting={isPresenting}
                    type="moderators"
                    containerId="mod-grid"
                  />
                </GridWrapper>
              </div>
            </div>

            <div className="flex flex-col flex-1 p-2 gap-4 min-h-0">
              <TeamSection
                title={hall?.affirmingTeamName}
                bgColor="bg-affirmative"
                borderColor="border-affirmative"
                participantIds={affRemaining}
                isPresenting={isPresenting}
                containerId="aff-grid"
                emptyPlaceholderSrc={affPlaceholder}
              />
              <TeamSection
                title={hall?.dissentingTeamName}
                bgColor="bg-negative"
                borderColor="border-negative"
                participantIds={negRemaining}
                isPresenting={isPresenting}
                containerId="neg-grid"
                emptyPlaceholderSrc={negPlaceholder}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  // No pins
  return (
    <div className="w-full flex flex-col overflow-hidden min-h-0" style={maxWrapperWidth}>
      <TopInfoBar />

      {isMobile ? (
        <div className="flex w-full flex-col flex-1 p-2 min-h-0 overflow-hidden gap-2">
          <TeamSection
            title={hall?.affirmingTeamName}
            bgColor="bg-affirmative"
            borderColor="border-affirmative"
            participantIds={removePinnedHallUsers(affirmativeUsers)}
            isPresenting={isPresenting}
            containerId="aff-grid"
            className="flex-1"
          />
          <TeamSection
            title={hall?.dissentingTeamName}
            bgColor="bg-negative"
            borderColor="border-negative"
            participantIds={removePinnedHallUsers(negativeUsers)}
            isPresenting={isPresenting}
            containerId="neg-grid"
            className="flex-1"
          />
          {moderatorUsers.length > 0 && (
            <GridWrapper
              id="mod-grid"
              className="w-full bg-gray-200 dark:bg-secondary rounded-lg overflow-hidden flex-[0.6] p-2"
            >
              <MemoizedParticipantGrid
                participantIds={removePinnedHallUsers(moderatorUsers)}
                isPresenting={isPresenting}
                type="moderators"
                containerId="mod-grid"
              />
            </GridWrapper>
          )}
        </div>
      ) : (
        <div className="flex w-full flex-col flex-1 p-2 min-h-0 overflow-hidden">
          <div className="flex w-full flex-col md:flex-row gap-2 overflow-hidden flex-1">
            <TeamSection
              title={hall?.affirmingTeamName}
              bgColor="bg-affirmative"
              borderColor="border-affirmative"
              participantIds={removePinnedHallUsers(affirmativeUsers)}
              isPresenting={isPresenting}
              containerId="aff-grid"
            />
            <TeamSection
              title={hall?.dissentingTeamName}
              bgColor="bg-negative"
              borderColor="border-negative"
              participantIds={removePinnedHallUsers(negativeUsers)}
              isPresenting={isPresenting}
              containerId="neg-grid"
            />
          </div>
          {moderatorUsers.length > 0 && (
            <div className="mt-2 w-full flex flex-col rounded-lg overflow-hidden border-2 border-purple-500 flex-1">
              <span className="w-full text-left py-1 pl-4 font-semibold tracking-wider text-white sticky top-0 z-10 bg-purple-600">
                Judges & Moderators
              </span>
              <GridWrapper id="mod-grid" className="bg-gray-200 dark:bg-secondary overflow-hidden flex-1 p-2">
                <MemoizedParticipantGrid
                  participantIds={removePinnedHallUsers(moderatorUsers)}
                  isPresenting={isPresenting}
                  type="moderators"
                  containerId="mod-grid"
                />
              </GridWrapper>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const MemorizedParticipantView = React.memo(
  ParticipantsViewer,
  (prev, next) => prev.isPresenting === next.isPresenting && prev.ignorePins === next.ignorePins,
)
export default MemorizedParticipantView
